import {
  CircularProgress,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";

export type QuestionCardProps = {
  children: React.ReactNode;
  id: number;
  name: string;
  date: string;
  getCurrentAnswers: (id: string) => void;
  isLoading?: boolean;
};

export const QuestionCard = ({
  id,
  name,
  date,
  getCurrentAnswers,
  isLoading,
  children,
}: QuestionCardProps) => {
  const theme = useTheme();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [shouldOpen, setShouldOpen] = useState(false);

  const handleClick = () => {
    if (!isOpen && !isLoading) {
      getCurrentAnswers(String(id));
      setShouldOpen(true);
    } else if (isOpen) {
      setOpen(false);
      setShouldOpen(false);
    }
  };

  useEffect(() => {
    if (!isLoading && shouldOpen && !isOpen) {
      setOpen(true);
      setShouldOpen(false);
    }
  }, [isLoading, shouldOpen, isOpen]);

  const dateColor =
    theme.palette.mode === "dark" ? theme.palette.text.secondary : "#666666";

  return (
    <Stack
      sx={{
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderTop: `1px solid ${theme.palette.divider}`,
        "&:last-of-type": {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Stack
        onClick={handleClick}
        py={1.5}
        px={1}
        gap={1}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          cursor: "pointer",
          transition: "background-color 0.3s ease, color 0.3s ease",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          color: isOpen ? "#F8B043" : undefined,
        }}
        role="button"
      >
        <Typography variant="body2">{name}</Typography>
        <Stack fontSize={12} color={dateColor}>
          ({dayjs(date).format("DD.MM.YYYY HH:mm")})
        </Stack>

        <Stack
          marginLeft="auto"
          flexShrink={0}
          sx={{
            transition: "transform 0.3s ease",
            transform: isOpen ? "rotateX(180deg)" : "rotate(0deg)",
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} />
          ) : (
            <KeyboardArrowDownIcon />
          )}
        </Stack>
      </Stack>
      <Collapse in={isOpen}>{children}</Collapse>
    </Stack>
  );
};
